import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, Upload, Loader2, Volume2, Sparkles, BookOpen, Bookmark } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { playTTS } from "@/lib/audio";
import { useLang } from "@/context/LangContext";
import SpeechCheck from "@/components/SpeechCheck";

export default function ScanLearn() {
  const nav = useNavigate();
  const { t } = useLang();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPreview(URL.createObjectURL(file)); setAnalysis(null); setLesson(null); setLoading(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await client.post("/scan-learn", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data.analysis);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Scan failed"); }
    finally { setLoading(false); e.target.value = ""; }
  };
  const makeLesson = async () => {
    setLessonLoading(true);
    try { const context = `Title: ${analysis.title}\n${analysis.explanation}\nVocab: ${(analysis.vocab || []).map((v) => `${v.word}=${v.translation}`).join(", ")}`;
      const { data } = await client.post("/scan-learn/lesson", { context }); setLesson(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLessonLoading(false); }
  };
  const savePhrase = async (word, translation) => {
    try { await client.post("/phrases", { target_text: word, translation, category: "Daily Life" }); toast.success(t("saved_short")); } catch { toast.error(t("couldnt_save")); }
  };

  return (
    <div className="min-h-screen w-full bg-[#04040c] flex justify-center">
      <div className="relative w-full max-w-[430px] min-h-screen app-bg flex flex-col px-5 py-6 overflow-y-auto no-scrollbar">
        <header className="flex items-center gap-3 mb-4">
          <button onClick={() => nav(-1)} data-testid="back-btn" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 press"><ArrowLeft size={20} /></button>
          <div><h1 className="font-heading text-xl font-extrabold text-white">{t("scan")}</h1><p className="text-sm text-slate-400">{t("scan_sub2b")}</p></div>
        </header>

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="scan-camera-input" />
        <input ref={galleryRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="scan-file-input" />

        {!preview && (
          <div className="mt-2 border-2 border-dashed border-white/15 rounded-3xl p-8 flex flex-col items-center gap-4 glass">
            <span className="w-16 h-16 rounded-2xl mel-gradient flex items-center justify-center text-white shadow-[0_12px_30px_-10px_rgba(37,99,235,0.8)]"><Camera size={28} /></span>
            <div className="text-center">
              <p className="font-semibold text-white">{t("scan_doc_title")}</p>
              <p className="text-sm text-slate-400 mt-0.5">{t("scan_sub")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 w-full">
              <button onClick={() => cameraRef.current?.click()} data-testid="scan-camera-btn" className="flex items-center justify-center gap-2 mel-gradient text-white font-semibold py-3 rounded-2xl press">
                <Camera size={18} /> {t("take_photo")}
              </button>
              <button onClick={() => galleryRef.current?.click()} data-testid="scan-upload-btn" className="flex items-center justify-center gap-2 glass text-cyan-200 font-semibold py-3 rounded-2xl press">
                <Upload size={18} /> {t("upload")}
              </button>
            </div>
          </div>
        )}

        {preview && (
          <div className="rounded-3xl overflow-hidden border border-white/10 relative">
            <img src={preview} alt="scan" className="w-full max-h-56 object-cover" data-testid="scan-preview" />
            <div className="absolute bottom-2 right-2 flex gap-2">
              <button onClick={() => cameraRef.current?.click()} className="glass-strong text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1"><Camera size={13} /> Retake</button>
              <button onClick={() => galleryRef.current?.click()} className="glass-strong text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1"><Upload size={13} /> Change</button>
            </div>
          </div>
        )}

        {loading && <div className="mt-6 flex flex-col items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={22} /> <span>{t("reading_img")}</span></div>}

        {analysis && (
          <div className="mt-5 space-y-4 animate-float-up" data-testid="scan-analysis">
            <div className="glass rounded-2xl p-4">
              <h2 className="font-heading font-bold text-white">{analysis.title}</h2>
              <p className="text-slate-300 mt-1 leading-relaxed">{analysis.explanation}</p>
              {analysis.action_needed && <p className="mt-3 bg-amber-500/10 border border-amber-400/30 rounded-xl p-3 text-sm text-amber-200 flex gap-2"><Sparkles size={15} className="shrink-0 mt-0.5" /> {analysis.action_needed}</p>}
            </div>
            {analysis.vocab?.length > 0 && (
              <div className="glass rounded-2xl p-4">
                <h3 className="font-heading font-bold text-white mb-2">{t("key_vocab")}</h3>
                <div className="space-y-2">{analysis.vocab.map((v, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-white/5 last:border-0 pb-2 last:pb-0">
                    <div><b className="text-white">{v.word}</b> <span className="text-slate-500">→</span> <span className="text-slate-300">{v.translation}</span></div>
                    <div className="flex gap-2">
                      <button onClick={() => playTTS(v.word)} className="text-cyan-300" data-testid={`vocab-listen-${i}`}><Volume2 size={17} /></button>
                      <button onClick={() => savePhrase(v.word, v.translation)} className="text-slate-500"><Bookmark size={17} /></button>
                    </div>
                  </div>
                ))}</div>
              </div>
            )}
            {!lesson && (
              <button onClick={makeLesson} disabled={lessonLoading} data-testid="create-lesson-btn" className="w-full mel-gradient text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_14px_34px_-14px_rgba(37,99,235,0.8)] disabled:opacity-60 press">
                {lessonLoading ? <Loader2 className="animate-spin" size={18} /> : <BookOpen size={18} />} {t("create_lesson")}
              </button>
            )}
            {lesson && (
              <div className="glass rounded-2xl p-4" data-testid="scan-lesson">
                <h3 className="font-heading font-bold text-white mb-3">{lesson.title}</h3>
                <div className="space-y-3">{lesson.steps?.map((s, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full mel-gradient text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm text-slate-400">{s.instruction}</p>
                      <p className="font-semibold text-white mt-0.5">{s.target_phrase}</p>
                      <p className="text-sm text-slate-500">{s.translation}</p>
                      <SpeechCheck phrase={s.target_phrase} translation={s.translation} testid={`lesson-speech-${i}`} />
                    </div>
                  </div>
                ))}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
